import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../hooks/useConfig';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetBand, apiGetTeam } from '../api';
import {
  apiGetPdfLayoutGlobalMeta,
  apiGetPdfLayoutEffectiveMeta,
  apiSavePdfLayoutGlobal,
  apiSavePdfLayoutBand,
  isLayoutConflict,
  type LayoutMeta,
} from '../api';
import { resolveBacklineImages } from '../lib/resolveBacklineImages';
import { layoutSignature } from '../lib/layoutSignature';
import { canEditLayoutFor, canEditGlobalLayout } from '../lib/layoutAccess';
import PdfPreview from '../components/PdfPreview';
import AutoTextarea from '../components/AutoTextarea';
import LayoutAccessDenied from '../components/LayoutAccessDenied';
import LayoutPresetsPanel from '../components/LayoutPresetsPanel';
import PageHeader from '../components/PageHeader';
import { LIGHT_FIXTURE_CATEGORIES, type TransportType } from '../types';
import { reportSaveError, reportSaveOk, isShowcaseMode } from '../lib/saveStatus';
import { buildPdfDoc } from './RiderPage';
import { resolveLightPlotsForPdf, sharedLightPlots } from '../lib/lightPlots';
import {
  createDefaultPdfLayout,
  mergePdfLayouts,
  fieldStyleOverride,
  fieldLabel,
  DEFAULT_COLUMN_WIDTHS,
  LIGHT_FIXTURE_COLUMN_IDS,
  type PdfLayout,
  type PdfLayoutField,
  type PdfLayoutSection,
  type PdfLayoutStyle,
  type PdfFieldStyleKey,
  type PdfSectionId,
  type RiderFontFamily,
} from '../lib/riderLayout';
import { withPageIcon } from '../lib/uiIcons';

/** Document font families offered by the style editor (all embedded). */
const RIDER_FONT_FAMILIES: RiderFontFamily[] = ['Roboto', 'Helvetica', 'Times', 'Courier'];
/** Per-section style keys the inline "section style" block can override
 *  (labelKey = the existing i18n label, so no duplicate strings).
 *  The page FOOTER is document-wide in pdfmake (a header/footer callback runs
 *  on every page) — its size/colour stay in the global Style section, they are
 *  deliberately NOT offered per section (a dead control). */
const SECTION_STYLE_NUM_FIELDS = [
  { key: 'headingFontSize', labelKey: 'headingSize' },
  { key: 'bodyFontSize', labelKey: 'bodySize' },
] as const;
const SECTION_STYLE_COLOR_FIELDS = [
  { key: 'headingColor', labelKey: 'headingColor' },
  { key: 'textColor', labelKey: 'textColor' },
  { key: 'ruleColor', labelKey: 'ruleColor' },
] as const;
/**
 * Per-FIELD style keys the inline "field style" block exposes (v0.558) —
 * exactly the same settings as a section, restricted to what makes sense for
 * ONE field: Font / Size / Bold / Italic / Color / Align. `kind` drives the
 * control: font = family select, number = size, tri = inherit/on/off select
 * (bold & italic are tri-state so "inherit" stays expressible), color, align.
 * A table column is a layout field too, so the same block drives the Input
 * List / Output List / Rack Usage columns.
 */
const FIELD_STYLE_FIELDS: { key: PdfFieldStyleKey; labelKey: string; kind: 'font' | 'number' | 'tri' | 'color' | 'align' }[] = [
  { key: 'fontFamily', labelKey: 'font', kind: 'font' },
  { key: 'bodyFontSize', labelKey: 'bodySize', kind: 'number' },
  { key: 'fieldBold', labelKey: 'bold', kind: 'tri' },
  { key: 'fieldItalic', labelKey: 'italic', kind: 'tri' },
  { key: 'textColor', labelKey: 'textColor', kind: 'color' },
  { key: 'fieldAlign', labelKey: 'alignment', kind: 'align' },
];

/**
 * Rider Layout Editor — admin-only WYSIWYG editor for the rider PDF layout.
 *
 * Two scopes:
 *  - "Global template"  : shared default for every band (api/pdf-layouts/global)
 *  - "This band"        : override for the active band (api/bands/:id/pdf-layout)
 *
 * The working layout is kept locally and the PDF preview (right column)
 * regenerates debounced on every edit — that is the WYSIWYG. Edits are
 * autosaved (debounced) and flushed before switching band/scope so nothing
 * is lost; the Save button persists immediately.
 *
 * The editor opens in the BAND scope when a band is active (band layout =
 * global template + band override) and falls back to the Global template
 * otherwise.
 */

const DEFAULT_TITLES: Record<PdfSectionId, string> = {
  title: 'Title',
  contact: 'Management / Advancing Contact',
  intro: 'Legal intro',
  'general-info': 'General Information',
  hospitality: 'Hospitality Rider',
  'technical-rider': 'Technical rider',
  sound: 'FOH & MON',
  iem: 'In-Ear Monitors',
  monitors: 'Monitors (wedges & subwoofer)',
  'audio-backline': 'Audio Specs',
  wireless: 'Wireless Microphones',
  backline: 'Backline',
  'mic-stands': 'Mic Stands',
  'mic-provided': 'Microphones Provided By',
  stageplan: 'Stageplan / Input-Output List',
  'input-list': 'Input List',
  'output-list': 'Output List',
  'rack-usage': 'Rack Usage',
  'stage-plot': 'Stage Plot',
  lights: 'Lights',
  'light-plot': 'Light Plot',
  'stage-design': 'Stage Design',
};

type Scope = 'global' | 'band';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * What each section renders in the rider PDF — shown under the section so the
 * editor mirrors the generated document (same order, same content).
 */
const SECTION_DETAILS: Record<PdfSectionId, string[]> = {
  title: [
    'Band logo (centered)',
    'Title: {bandName} TECH & HOSPITALITY RIDER 2026',
    'Subtitle: (version: van / plane / tourbus, N horns)',
  ],
  contact: [
    'Management / Advancing Contact (from Team, manager tag)',
    'Name / email / phone — centered, right under the header',
  ],
  intro: [
    'Legal intro sentence (italic), at the very top of the rider',
    'Above General Information, toggleable / reorderable as its own section',
  ],
  'general-info': [
    'Travel party: "N people on the road: X musicians, crew breakdown"',
    'SHOW & SETUP: show duration / installation / soundcheck / changeover / breakdown',
    'Security / Merchandising / Notes lines',
  ],
  hospitality: [
    'Bullet list ●: Meals needed, FOOD ALLERGIES, NON-FOOD ALLERGIES',
    'STAGE TOWELS / DRESSING-ROOM TOWELS / SIDE-STAGE WATER (per selected person)',
    'DRESSING ROOMS / VARIOUS / SECURITY / DINNER (from Hospitality config)',
  ],
  'technical-rider': [
    'Section title (default “Technical rider”)',
    'Technical intro text — the clause requesting the exact equipment in this rider',
  ],
  sound: [
    'FOH Console + FOH requirements / PA MIX OUTPUT (rack + stereo pair carrying the main mix to the house PA) / PA System / Monitor Console + Monitor requirements / Monitor System (+ "provided by the band" when set)',
    'Sound limit (dB) — from the Sound config',
  ],
  iem: [
    'System (+ "provided by the band" when set)',
    'Units needed (auto from selected people)',
    'Frequency Range / Bodypacks (transmitters + spare) / Wired in-ears / Note',
  ],
  monitors: [
    'Monitors (wedges): total from the selected people\u2019s cards, venue/band provider',
    'Subwoofer: venue/band provider',
    'Wedges of a MON engineer carry "for PFL"',
  ],
  'audio-backline': [
    'Specs: FOH Cables / Stage Cables / Audio Cables / Network / Power',
    'Microphones & Frequency specs live under Wireless Transmitters (moved v0.642)',
  ],
  wireless: [
    'Microphones specs / Frequency specs (free-text lines)',
    'Table: Person | Instrument | Transmitter | Provided by (wireless tracks only)',
  ],
  backline: [
    'Columns (show / hide / reorder): Qty | Type | Item | Model | Provided by | Notes',
    'Table aggregated from musician + crew cards; band-level Items List included',
    'Type is cleaned for slugs (keyboard_stand → Keyboard stand) and printed on EVERY row — including items with no label of their own (2026-10-15: that cell used to stay blank there, which read as a missing field)',
  ],
  'mic-stands': [
    'Columns (show / hide / reorder / resize): Qty | Type | Boom | Destination | Provided by',
    'Table of the stands the venue must provide (aggregated from instrument tracks; a clip is never a stand)',
    'Destination = who the stand is for: derived from the musician carrying the instrument, or the free-text “Destination” set on the track in config',
    'The table is placed at the position of the first enabled column — Add space / Add text still work',
  ],
  'mic-provided': [
    'Summary line: "N stands/microphones provided by The Band / The Venue"',
  ],
  stageplan: [
    'Wrapper heading only — Input List, Output List and Stage Plot follow',
  ],
  'input-list': [
    'Table: Ch | Track | Rack | Mic | Connection | Transmitter | Stand | Provided by | Notes',
    'Rows = selected musicians\' instrument tracks, padded to max channel (min 24)',
  ],
  'output-list': [
    'Table: Rack | Ch | Track | Type | Notes (sidefills, IEM, wedges, subs)',
  ],
  'rack-usage': [
    'Table: Rack | Inputs used/capacity | Outputs used/capacity | Occupants',
    'Per-rack occupancy summary across the whole band (complement to Input/Output Lists)',
    '⚠ marks a rack over capacity',
  ],
  'stage-plot': [
    'Stage plot image (from the Stage Plot editor), starts a new page by default',
  ],
  lights: [
    'Console / Haze Machine',
    'Fixtures: all lighting categories — columns (show / hide / reorder / resize): Qty | Model | Description | Notes',
    'DMX / Notes lines',
  ],
  'light-plot': [
    'Light plot image (only when configured)',
  ],
  'stage-design': [
    'Stage dimensions (band’s requested ideal size)',
    'Backdrop + dimensions',
    'Risers: each “Name: W × D × H — position”',
    'Rug / Accessories / Chairs / Tools / Notes',
  ],
};

/**
 * Which fixed texts are edited INSIDE a section (they appear there in the PDF).
 * Key = section id → list of PdfLayoutTexts keys rendered inline in that block.
 */
const SECTION_TEXT_FIELDS: Partial<Record<PdfSectionId, (keyof PdfLayout['texts'])[]>> = {
  title: ['titleSuffix', 'versionTemplate'],
  contact: ['contactLabel', 'contactOverrideName', 'contactOverrideEmail', 'contactOverridePhone'],
  // The patch-marks legend explains the coloured Ch cells of the Input List —
  // edited inline in that section (2026-10-23, user).
  'input-list': [],
  // The legal intro sentence is a separate section (Rider-Intro-Section-Spec
  // 2026-09-16) above General Information. It is NOT edited inline here — only
  // its "edit" link to the config page is shown (user, 2026-09-16).
};

const TEXT_FIELD_LABELS: Record<Exclude<keyof PdfLayout['texts'], 'inputListPatchLegends' | 'outputListPatchLegends'>, string> = {
  // Technical intro (own TECHNICAL RIDER section): edited on the General config
  // page via the field's "edit" link, but the key must still carry a label.
  techIntroText: 'configGeneral.techIntro',
  titleSuffix: 'riderEditor.titleSuffix',
  versionTemplate: 'riderEditor.versionTemplate',
  contactLabel: 'riderEditor.contactLabel',
  contactOverrideName: 'riderEditor.contactOverrideName',
  contactOverrideEmail: 'riderEditor.contactOverrideEmail',
  contactOverridePhone: 'riderEditor.contactOverridePhone',
  introText: 'riderEditor.introText',
  footerLeftTemplate: 'riderEditor.footerLeftTemplate',
  footerCenterTemplate: 'riderEditor.footerCenterTemplate',
  footerRightTemplate: 'riderEditor.footerRightTemplate',
  footerEnabled: 'riderEditor.footerEnabled',
  headerEnabled: 'riderEditor.headerEnabled',
  headerLeftTemplate: 'riderEditor.headerLeftTemplate',
  headerCenterTemplate: 'riderEditor.headerCenterTemplate',
  headerRightTemplate: 'riderEditor.headerRightTemplate',
  showTourName: 'riderEditor.showTourName',
};

export default function RiderEditorPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { activeProfileId, getProfile } = useProfileContext();
  const { config } = useConfig();
  const { musicians } = useMusicians();
  const { crew } = useCrew();
  const navigate = useNavigate();
  const bandBase = activeProfileId ? `/band/${activeProfileId}` : '';

  // Platform admin, the read-only showcase (demo) account — which is allowed to
  // OPEN the layout editors (a sample of the feature) but never saves —, a band
  // OWNER, or a member the owner granted the `layout.rider` capability on this
  // band. Exactly the server rule
  // (`canWriteLayout(req, bandId, 'rider')` in server/authz.js). (2026-09-25)
  const isSuperAdmin = user?.global_role === 'superadmin';
  const isShowcase = !!user?.is_showcase;
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const canEditLayouts = canEditLayoutFor({
    isSuperAdmin,
    isShowcase,
    bandRole: isSuperAdmin ? 'owner' : activeProfile?.my_role,
    permissions: activeProfile?.permissions,
    layoutAccess: activeProfile?.layout_access,
  }, 'rider');
  // The GLOBAL template is shared by every band, so `layout_access` does NOT
  // open it: admin-only, and its tab is hidden for anybody else. A non-admin
  // starts on the BAND scope (the tab it can actually save).
  const canEditGlobalTemplate = canEditGlobalLayout(isSuperAdmin);
  const [scope, setScope] = useState<Scope>(() => (canEditGlobalTemplate ? 'global' : 'band'));
  const [layout, setLayout] = useState<PdfLayout>(() => createDefaultPdfLayout());
  const [loaded, setLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  // When the editor opens inside a band, default to the BAND scope (the band
  // layout = global template + band override). Only auto-switch once — a
  // manual Global/Band choice by the user is never overridden.
  const autoScopedRef = useRef(false);
  // Canonical signature of the layout as last LOADED or SAVED. This — not an
  // object identity and not a hand-set `dirty` flag — is what decides whether a
  // write is needed: a scope switch reloads (and re-merges) the layout into a
  // brand-new object even when the user edited nothing, so identity/stale-flag
  // tests let the flush persist an untouched layout over the scope being left
  // (that is how the GLOBAL template got overwritten — see
  // `src/lib/layoutSignature.ts`).
  const savedSigRef = useRef<string | null>(null);
  // Concurrency token of the layout we last LOADED/SAVED (`pdf_layouts.last_modified`).
  // Sent on every write: the server answers 409 instead of overwriting when
  // another tab/session wrote this layout in the meantime (2026-09-16).
  const savedLastModifiedRef = useRef<number | null>(null);
  // Always-fresh signature of the WORKING layout (assigned on every render,
  // like layoutRef below) so the flush effect can compare inside its cleanup.
  const layoutSigRef = useRef<string>('');
  layoutSigRef.current = layoutSignature(layout);
  // Always-fresh layout/scope/band refs so the load effect can flush pending
  // edits with the PREVIOUS scope/band before switching.
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const bandRef = useRef(activeProfileId);
  bandRef.current = activeProfileId;

  // Preview state — the docDef is rasterized to per-page images by <PdfPreview>
  // so every page renders on iOS (an iframe shows only page 1). (2026-08-13)
  const [pdfDoc, setPdfDoc] = useState<any | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [bandLogoUrl, setBandLogoUrl] = useState<string | null>(null);
  const [teamManager, setTeamManager] = useState<{ name?: string; email?: string; phone?: string } | null>(null);
  // Diet lives on the Team identity (team_members), not the rider card. Map
  // team_member id -> { diet, dietNotes } so the hospitality PDF section in the
  // layout-editor preview shows the same food allergies as the real rider.
  // (Same as RiderPage — without this the editor preview omits team diets.)
  const [dietById, setDietById] = useState<Record<string, { diet?: string; dietNotes?: string }>>({});
  const genTokenRef = useRef(0);

  const bandName = activeProfileId ? getProfile(activeProfileId)?.name : undefined;
  // Band name used by the PREVIEW. The global template is shared, so the preview
  // printed the literal "Global Template" wherever {bandName}/{bandName} tokens
  // appear (title, running header, footer) — which reads as a bug ("{bandName}
  // affiche GLOBAL TEMPLATE"). Render the CURRENT band's name when one is loaded
  // (a realistic sample; the preview badge already states the scope) and a
  // neutral sample otherwise.
  const previewBandName = bandName || t('riderEditor.sampleBandName');

  // Auto-default to the BAND scope when a band is active (opening the editor
  // from a band should edit that band's layout, not the global template).
  // Runs once — the user can still switch to Global manually.
  useEffect(() => {
    if (autoScopedRef.current) return;
    if (activeProfileId) {
      autoScopedRef.current = true;
      setScope('band');
    }
  }, [activeProfileId]);

  // Fetch the layout of the scope being edited — data PLUS the concurrency
  // token (`last_modified`) that every write must echo back.
  const fetchLayoutForScope = useCallback(async (): Promise<LayoutMeta> => {
    if (scope === 'global') return apiGetPdfLayoutGlobalMeta();
    if (activeProfileId) return apiGetPdfLayoutEffectiveMeta(activeProfileId);
    return { data: null, lastModified: null };
  }, [scope, activeProfileId]);

  // Apply a loaded layout: merged over the defaults, then baselined BOTH for
  // the dirty test (signature) and for the concurrency token.
  const applyLoadedLayout = useCallback((meta: LayoutMeta | null) => {
    const next = meta?.data
      ? mergePdfLayouts(createDefaultPdfLayout(), meta.data)
      : createDefaultPdfLayout();
    setLayout(next);
    savedSigRef.current = layoutSignature(next);
    savedLastModifiedRef.current = meta?.lastModified ?? null;
  }, []);

  // Load the saved layout for the current scope (fall back to defaults when
  // no row exists yet). Reset preview state on scope/band change.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      setLoaded(false);
      setSaveStatus('idle');
      setSaveError(null);
      setPdfDoc(null);
      setPdfError(null);
      try {
        const saved = await fetchLayoutForScope();
        if (cancelled) return;
        applyLoadedLayout(saved);
      } catch (err: any) {
        if (!cancelled) {
          setSaveError(err?.message || t('riderEditor.failedLoadLayout'));
          applyLoadedLayout(null);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
    // fetchLayoutForScope/applyLoadedLayout are derived from scope+activeProfileId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, activeProfileId]);

  // FLUSH pending edits when switching scope/band (and on unmount): the
  // cleanup captures THIS render's scope/band, so it persists the edits under
  // the scope they were made in, before the new scope loads over them.
  useEffect(() => {
    const scopeAtRender = scope;
    const bandAtRender = activeProfileId;
    // `loaded` of THIS render: the initial mount is dirty by construction
    // (savedSigRef starts null), and the editor auto-switches to the
    // band scope on mount. Flushing before the first load resolved wrote the
    // DEFAULT layout over the scope being left — i.e. simply OPENING the editor
    // from a band URL reset the GLOBAL template (custom texts + the running
    // header) to the defaults (reproduced 2026-09-15). Nothing can have been
    // edited before the first load finishes, so skip the flush until then.
    const loadedAtRender = loaded;
    return () => {
      // Showcase (demo) account: never flush on scope/band switch — nothing is
      // ever persisted for the demo. (2026-09-15)
      if (isShowcaseMode()) return;
      if (!loadedAtRender) return;
      // Flush ONLY a layout the user actually changed: compare the canonical
      // signature of the working layout with the last loaded/saved one. A scope
      // switch alone (which reloads the layout into a new object) is NOT an edit
      // and must never write — that is what used to clobber the GLOBAL template.
      if (savedSigRef.current === null || savedSigRef.current === layoutSigRef.current) return;
      try {
        if (scopeAtRender === 'global') {
          void apiSavePdfLayoutGlobal(layoutRef.current, savedLastModifiedRef.current);
        } else if (bandAtRender) {
          void apiSavePdfLayoutBand(bandAtRender, layoutRef.current, savedLastModifiedRef.current);
        }
        savedSigRef.current = layoutSigRef.current;
      } catch {
        // Best-effort flush; the autosave effect retries the current scope.
      }
    };
  }, [scope, activeProfileId, loaded]);

  // Band logo for the PDF header (image_url preferred — same fallback as RiderPage).
  useEffect(() => {
    let cancelled = false;
    setBandLogoUrl(null);
    if (!activeProfileId) return;
    apiGetBand(activeProfileId)
      .then((b) => { if (!cancelled) setBandLogoUrl(b?.image_url || b?.logo || b?.image || null); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Management / Advancing contact from the Team page (same as RiderPage).
  useEffect(() => {
    let cancelled = false;
    setTeamManager(null);
    if (!activeProfileId) return;
    apiGetTeam({ band: activeProfileId, tags: ['manager'] })
      .then((list) => {
        if (cancelled) return;
        const m = list[0];
        if (m) setTeamManager({ name: m.name || m.real_name || m.nickname || '', email: m.email || '', phone: m.phone || '' });
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Load the Team identity diets (team_members) so the layout-editor preview
  // shows the same food allergies as the real rider PDF (same as RiderPage).
  useEffect(() => {
    if (!activeProfileId) { setDietById({}); return; }
    let cancelled = false;
    setDietById({});
    void apiGetTeam({ band: activeProfileId }).then((members) => {
      if (cancelled) return;
      const map: Record<string, { diet?: string; dietNotes?: string }> = {};
      for (const m of members) {
        map[m.id] = { diet: m.diet || '', dietNotes: m.diet_notes || '' };
      }
      setDietById(map);
    }).catch(() => { if (!cancelled) setDietById({}); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // ---- Field content detection (2026-08-11) ----
  // Tells whether a field would actually emit content in the generated PDF,
  // given the current band config / selection. The layout editor uses this to
  // visually uncheck fields that are empty (so a missing value is obvious at a
  // glance) without touching the saved layout.
  const transport = (config?.rider?.transport as TransportType | '') || '';
  const cfgGeneral = config?.general?.[transport as TransportType] || {} as any;
  const cfgHospitality = config?.hospitality?.[transport as TransportType] || {} as any;
  const cfgSound = config?.sound?.[transport as TransportType] || {} as any;
  const cfgIem = config?.iem?.[transport as TransportType] || {} as any;
  const cfgSpecs = config?.specs?.[transport as TransportType] || {} as any;
  const cfgLights = config?.lights?.[transport as TransportType] || {} as any;
  const cfgStageDesign = config?.stageDesign?.[transport as TransportType] || {} as any;
  const fieldHasContent = useCallback((fieldId: string): boolean => {
    const isSpacerF = (fid: string) => fid === 'spacer' || fid.endsWith('.spacer');
    const isTextF = (fid: string) => fid === 'text' || fid.endsWith('.text');
    if (isSpacerF(fieldId) || isTextF(fieldId)) return true;
    const texts = layout?.texts || {};
    const selMus = (config?.rider?.activeMusicianIds || []);
    const selCrew = (config?.rider?.activeCrewIds || []);
    switch (fieldId) {
      case 'intro.intro': return !!(texts.introText || '').trim();
      // Technical intro: the band's layout key, else the legacy SoundConfig value
      // (the field is never "empty" while the fallback prints something).
      case 'technical-rider.intro':
        return typeof texts.techIntroText === 'string'
          ? !!texts.techIntroText.trim()
          : true;
      case 'general-info.intro': return !!(texts.introText || '').trim();
      case 'general-info.travel-party': return selMus.length > 0 || selCrew.length > 0;
      case 'general-info.show-duration': return !!cfgGeneral.duration;
      case 'general-info.installation': return !!cfgGeneral.installationTimeMinutes;
      case 'general-info.soundcheck': return !!cfgGeneral.soundcheckDurationMinutes;
      case 'general-info.changeover': return !!cfgGeneral.changeoverDurationMinutes;
      case 'general-info.breakdown': return !!cfgGeneral.breakdownDurationMinutes;
      case 'general-info.special-requests': return !!cfgGeneral.specialRequests;
      case 'general-info.security': return !!cfgGeneral.security;
      case 'general-info.merchandise': return !!cfgGeneral.merchandise;
      case 'general-info.notes': return !!cfgGeneral.notes;
      case 'contact.contact': {
        const hasOverride = !!(texts.contactOverrideName?.trim() || texts.contactOverrideEmail?.trim() || texts.contactOverridePhone?.trim());
        return hasOverride || !!teamManager?.name || !!teamManager?.email || !!teamManager?.phone;
      }
      case 'hospitality.meals': return true; // always emitted (total people)
      case 'hospitality.stage-towels': return (musicians || []).some((m: any) => Number(m.towels) > 0) || (crew || []).some((c: any) => Number(c.towels) > 0);
      case 'hospitality.dressing-towels': return (musicians || []).some((m: any) => Number(m.dressingRoomTowels) > 0) || (crew || []).some((c: any) => Number(c.dressingRoomTowels) > 0);
      case 'hospitality.side-water': return (musicians || []).some((m: any) => Number(m.water) > 0) || (crew || []).some((c: any) => Number(c.water) > 0);
      case 'hospitality.dressing-rooms': return !!cfgHospitality.dressingRooms;
      case 'hospitality.various': return !!cfgHospitality.various;
      case 'hospitality.security': return !!cfgHospitality.security;
      case 'hospitality.breakfast': return !!cfgHospitality.breakfast;
      case 'hospitality.lunch': return !!cfgHospitality.lunch;
      case 'hospitality.dinner': return !!cfgHospitality.dinner;
      case 'sound.foh': return !!cfgSound.fohConsole;
      // v0.594 : sortie du mix principal (rack + paire stéréo choisie dans la config)
      case 'sound.pa-mix-output': return !!(cfgSound.paMixRackId && cfgSound.paMixOutStart);
      case 'sound.pa': return !!cfgSound.paSystem;
      case 'sound.monitor': return !!cfgSound.monitorSystem;
      case 'sound.limit': return !!cfgGeneral.soundLimitDb;
      case 'iem.system': return true; // 'TBD' fallback always emitted
      case 'iem.mix': return !!cfgIem.mixType;
      case 'iem.units': return (musicians || []).some((m: any) => m.iemType && m.iemType !== 'none');
      case 'iem.frequency': return !!cfgIem.frequencyRange;
      case 'iem.bodypacks': return (musicians || []).some((m: any) => m.iemType && m.iemType !== 'none') || Number(cfgIem.extraBodypacks || 0) > 0;
      case 'iem.wired': return (musicians || []).some((m: any) => m.iemType === 'wired') || (crew || []).some((c: any) => c.iemType === 'wired');
      case 'iem.note': return !!cfgIem.notes;
      case 'wireless.mics': return !!cfgSpecs.microphonesSpecs;
      case 'wireless.frequency': return !!cfgSpecs.frequencyRange;
      case 'audio-backline.cable': return !!cfgSpecs.cableSpec;
      case 'audio-backline.dmx': return !!cfgSpecs.audioCablesDetails;
      case 'audio-backline.network': return !!cfgSpecs.networkRequirements;
      case 'audio-backline.power': return !!cfgSpecs.powerRequirements;
      case 'lights.console': return !!cfgLights.console;
      case 'lights.haze': return !!cfgLights.hazeMachine;
      case 'lights.fixtures': {
        const fx: any = cfgLights.fixtures || {};
        return LIGHT_FIXTURE_CATEGORIES.some(({ value: cat }) => (Array.isArray(fx[cat]) ? fx[cat].length : false));
      }
      case 'lights.dmx': return !!cfgLights.dmxLine;
      case 'lights.notes': return !!cfgLights.notes;
      case 'stage-design.dimensions': return !!cfgStageDesign.stageDimensions;
      case 'stage-design.backdrop': return !!(cfgStageDesign.backdrop || cfgStageDesign.backdropDimensions);
      case 'stage-design.backdrop-dimensions': return !!cfgStageDesign.backdropDimensions;
      case 'stage-design.risers': return (cfgStageDesign.risers || []).some((r: any) => r && (r.name || r.width || r.depth || r.height || r.position));
      case 'stage-design.rug': return !!cfgStageDesign.rug;
      case 'stage-design.accessories': return !!cfgStageDesign.accessories;
      case 'stage-design.chairs': return !!cfgStageDesign.chairs;
      case 'stage-design.tools': return !!cfgStageDesign.tools;
      case 'stage-design.notes': return !!cfgStageDesign.notes;
      default: return true;
    }
  }, [config, musicians, crew, teamManager, transport, layout, cfgGeneral, cfgHospitality, cfgSound, cfgIem, cfgSpecs, cfgLights, cfgStageDesign]);

  // ---- Section editing helpers (immutable updates on the working copy) ----
  const isSpacer = (fieldId: string) => fieldId === 'spacer' || fieldId.endsWith('.spacer');
  const isTextField = (fieldId: string) => fieldId === 'text' || fieldId.endsWith('.text');
  const updateSections = useCallback((fn: (sections: PdfLayoutSection[]) => PdfLayoutSection[]) => {
    setLayout((prev) => ({ ...prev, sections: fn(prev.sections) }));
  }, []);

  const moveSection = useCallback((index: number, dir: -1 | 1) => {
    updateSections((sections) => {
      const target = index + dir;
      if (target < 0 || target >= sections.length) return sections;
      const next = [...sections];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, [updateSections]);

  const toggleEnabled = useCallback((index: number) => {
    updateSections((sections) =>
      sections.map((s, i) => (i === index ? { ...s, enabled: !s.enabled } : s)),
    );
  }, [updateSections]);

  const setTitle = useCallback((index: number, title: string) => {
    updateSections((sections) =>
      sections.map((s, i) => (i === index ? { ...s, title: title || null } : s)),
    );
  }, [updateSections]);

  const togglePageBreak = useCallback((index: number) => {
    updateSections((sections) =>
      sections.map((s, i) => (i === index ? { ...s, pageBreakBefore: !s.pageBreakBefore } : s)),
    );
  }, [updateSections]);

  // ---- Per-section style override ----
  // Open/close the inline style block of a section card (UI state only, never
  // persisted — the persisted part is `section.styleOverride`).
  const toggleSectionOverride = useCallback((sectionId: string) => {
    setOpenStyleOverrides((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  // Set (value) or clear (undefined / '') ONE style key of ONE section. Clearing
  // removes the key from the override so the section inherits the global style
  // again; an override left with no key is dropped entirely (back to inherit).
  const updateSectionStyleOverride = useCallback((
    index: number,
    key: keyof PdfLayoutStyle,
    value: string | number | boolean | undefined,
  ) => {
    updateSections((sections) =>
      sections.map((s, i) => {
        if (i !== index) return s;
        const next: Record<string, unknown> = { ...(s.styleOverride || {}) };
        if (value === undefined || value === '') delete next[key as string];
        else next[key as string] = value;
        const copy: PdfLayoutSection = { ...s };
        if (Object.keys(next).length > 0) copy.styleOverride = next as Partial<PdfLayoutStyle>;
        else delete copy.styleOverride;
        return copy;
      }),
    );
  }, [updateSections]);

  // Drop the whole override of a section → it renders with the global style.
  const resetSectionStyleOverride = useCallback((index: number) => {
    updateSections((sections) =>
      sections.map((s, i) => {
        if (i !== index) return s;
        const copy: PdfLayoutSection = { ...s };
        delete copy.styleOverride;
        return copy;
      }),
    );
  }, [updateSections]);

  // ---- Per-FIELD style override (v0.558) ----
  // Open/close the inline style block of ONE field row (UI state only — the
  // persisted part is `field.styleOverride`). Keyed by section id + field index
  // so the block follows its field.
  const toggleFieldStyle = useCallback((sectionIndex: number, fieldIndex: number) => {
    const sectionId = layoutRef.current?.sections?.[sectionIndex]?.id ?? String(sectionIndex);
    const key = `${sectionId}:${fieldIndex}`;
    setOpenFieldStyles((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Set (value) or clear (undefined / '') ONE style key of ONE field. Clearing
  // removes the key so the field inherits the section/global style again; an
  // override left with no key is dropped entirely.
  const updateFieldStyleOverride = useCallback((
    sectionIndex: number,
    fieldIndex: number,
    key: keyof PdfLayoutStyle,
    value: string | number | boolean | undefined,
  ) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return {
          ...s,
          fields: s.fields.map((f, fi) => {
            if (fi !== fieldIndex) return f;
            const next: Record<string, unknown> = { ...(f.styleOverride || {}) };
            if (value === undefined || value === '') delete next[key as string];
            else next[key as string] = value;
            const copy: PdfLayoutField = { ...f };
            if (Object.keys(next).length > 0) copy.styleOverride = next as Partial<PdfLayoutStyle>;
            else delete copy.styleOverride;
            return copy;
          }),
        };
      });
      return { ...prev, sections };
    });
  }, []);

  // Reset = the field inherits again: drop the whole per-field override AND the
  // legacy flat keys of a free-text field (its own B / I / size / align
  // toolbar), so nothing keeps the field away from the section/global style.
  const resetFieldStyleOverride = useCallback((sectionIndex: number, fieldIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return {
          ...s,
          fields: s.fields.map((f, fi) => {
            if (fi !== fieldIndex) return f;
            const copy: PdfLayoutField = { ...f };
            delete copy.styleOverride;
            delete copy.fontSize;
            delete copy.bold;
            delete copy.italic;
            delete copy.align;
            return copy;
          }),
        };
      });
      return { ...prev, sections };
    });
  }, []);

  // Set (a number) or clear (undefined) the WIDTH of ONE table-column field
  // (v0.644). Clearing deletes `field.width` → the engine falls back to the
  // column's DEFAULT width ('auto' / a number / '*'). Applies to the COLUMN
  // fields of input-list / output-list / rack-usage / backline (their `fields`
  // list doubles as the table's columns). Autosave persists it like any edit.
  const updateFieldWidth = useCallback((sectionIndex: number, fieldIndex: number, width: number | undefined) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return {
          ...s,
          fields: s.fields.map((f, fi) => {
            if (fi !== fieldIndex) return f;
            const copy: PdfLayoutField = { ...f };
            if (width === undefined || !Number.isFinite(width) || width <= 0) delete copy.width;
            else copy.width = width;
            return copy;
          }),
        };
      });
      return { ...prev, sections };
    });
  }, []);

  // The control of ONE per-field style key, shared by the two rows of the field
  // block (Font/Size/Bold, then Italic/Color/Align) so a key can never be
  // rendered in one row only. kind: font = family select, number = size,
  // tri = inherit/on/off (bold & italic — "inherit" stays expressible), color,
  // align.
  const fieldStyleControl = (
    sectionIndex: number,
    fieldIndex: number,
    key: PdfFieldStyleKey,
    kind: 'font' | 'number' | 'tri' | 'color' | 'align',
    fov: Partial<PdfLayoutStyle>,
  ) => {
    if (kind === 'font') {
      return (
        <select
          className="form-input"
          value={(fov[key] as string) ?? ''}
          onChange={(e) => updateFieldStyleOverride(sectionIndex, fieldIndex, key, e.target.value || undefined)}
        >
          <option value="">{t('riderEditor.styleInherit')}</option>
          {RIDER_FONT_FAMILIES.map((fam) => (
            <option key={fam} value={fam}>{fam}</option>
          ))}
        </select>
      );
    }
    if (kind === 'number') {
      return (
        <input
          type="number"
          step="0.5"
          className="form-input"
          value={(fov[key] as number) ?? ''}
          placeholder={String((layout.style[key] as number) ?? '')}
          onChange={(e) => updateFieldStyleOverride(sectionIndex, fieldIndex, key, e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    }
    if (kind === 'tri') {
      return (
        <select
          className="form-input"
          value={fov[key] === undefined ? '' : (fov[key] ? 'on' : 'off')}
          onChange={(e) => updateFieldStyleOverride(
            sectionIndex, fieldIndex, key,
            e.target.value === '' ? undefined : e.target.value === 'on',
          )}
        >
          <option value="">{t('riderEditor.styleInherit')}</option>
          <option value="on">{t('riderEditor.styleOn')}</option>
          <option value="off">{t('riderEditor.styleOff')}</option>
        </select>
      );
    }
    if (kind === 'color') {
      return (
        <input
          type="color"
          className="form-input"
          style={{ padding: 0, height: 38 }}
          value={(fov[key] as string) ?? (layout.style.textColor as string)}
          onChange={(e) => updateFieldStyleOverride(sectionIndex, fieldIndex, key, e.target.value)}
        />
      );
    }
    return (
      <select
        className="form-input"
        value={(fov[key] as string) ?? ''}
        onChange={(e) => updateFieldStyleOverride(sectionIndex, fieldIndex, key, e.target.value || undefined)}
      >
        <option value="">{t('riderEditor.styleInherit')}</option>
        <option value="left">{t('riderEditor.left')}</option>
        <option value="center">{t('riderEditor.center')}</option>
        <option value="right">{t('riderEditor.right')}</option>
      </select>
    );
  };

  // ---- Field-level editing (inside a section) ----
  const moveField = useCallback((sectionIndex: number, fieldIndex: number, dir: -1 | 1) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        const target = fieldIndex + dir;
        if (target < 0 || target >= s.fields.length) return s;
        const next = [...s.fields];
        [next[fieldIndex], next[target]] = [next[target], next[fieldIndex]];
        return { ...s, fields: next };
      });
      return { ...prev, sections };
    });
  }, []);

  const toggleField = useCallback((sectionIndex: number, fieldIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return {
          ...s,
          fields: s.fields.map((f, fi) => (fi === fieldIndex ? { ...f, enabled: !f.enabled } : f)),
        };
      });
      return { ...prev, sections };
    });
  }, []);

  // Insert a spacer field at the TOP of the section's field list (new spaces
  // appear first, then you move them down where needed). Spacers render as a
  // vertical gap in the PDF; height is adjustable via the size input.
  const addSpacer = useCallback((sectionIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        const spacer: PdfLayoutField = { id: 'spacer', enabled: true, label: 'Space', link: null, size: 4 };
        const fields = Array.isArray(s.fields) ? [...s.fields] : [];
        fields.unshift(spacer);
        return { ...s, fields };
      });
      return { ...prev, sections };
    });
  }, []);

  const updateSpacerSize = useCallback((sectionIndex: number, fieldIndex: number, size: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return {
          ...s,
          fields: s.fields.map((f, fi) => (fi === fieldIndex ? { ...f, size } : f)),
        };
      });
      return { ...prev, sections };
    });
  }, []);

  const removeSpacer = useCallback((sectionIndex: number, fieldIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return { ...s, fields: s.fields.filter((_, fi) => fi !== fieldIndex) };
      });
      return { ...prev, sections };
    });
  }, []);

  // Insert a free-text field at the TOP of the section's field list. Renders
  // as a paragraph in the PDF with basic formatting (bold/italic/size/align).
  const addText = useCallback((sectionIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        const tf: PdfLayoutField = { id: 'text', enabled: true, label: 'Text', link: null, textContent: '' };
        const fields = Array.isArray(s.fields) ? [...s.fields] : [];
        fields.unshift(tf);
        return { ...s, fields };
      });
      return { ...prev, sections };
    });
  }, []);

  const updateTextField = useCallback((sectionIndex: number, fieldIndex: number, patchObj: Partial<PdfLayoutField>) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        return {
          ...s,
          fields: s.fields.map((f, fi) => (fi === fieldIndex ? { ...f, ...patchObj } : f)),
        };
      });
      return { ...prev, sections };
    });
  }, []);

  // Duplicate a spacer/text field: insert a deep copy right after the original.
  const duplicateField = useCallback((sectionIndex: number, fieldIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields)) return s;
        const src = s.fields[fieldIndex];
        if (!src) return s;
        const copy: PdfLayoutField = { ...src, enabled: src.enabled };
        if (src.id === 'spacer') copy.size = src.size ?? 4;
        const fields = [...s.fields];
        fields.splice(fieldIndex + 1, 0, copy);
        return { ...s, fields };
      });
      return { ...prev, sections };
    });
  }, []);

  // Which section cards have their inline "section style" (override) block
  // open, keyed by section id (an index key would follow the wrong card when
  // sections are reordered).
  const [openStyleOverrides, setOpenStyleOverrides] = useState<Record<string, boolean>>({});
  // Per-FIELD style blocks currently expanded, keyed `<sectionId>:<fieldIndex>`
  // (UI state only — the persisted part is `field.styleOverride`).
  const [openFieldStyles, setOpenFieldStyles] = useState<Record<string, boolean>>({});

  // Drag & drop reordering for field rows (same section only).
  const [dragField, setDragField] = useState<{ sectionIndex: number; fieldIndex: number } | null>(null);
  const moveFieldTo = useCallback((sectionIndex: number, targetIndex: number) => {
    setLayout((prev) => {
      const sections = prev.sections.map((s, i) => {
        if (i !== sectionIndex || !Array.isArray(s.fields) || !dragField || dragField.sectionIndex !== sectionIndex) return s;
        const from = dragField.fieldIndex;
        if (from === targetIndex) return s;
        const fields = [...s.fields];
        const [moved] = fields.splice(from, 1);
        fields.splice(targetIndex, 0, moved);
        return { ...s, fields };
      });
      return { ...prev, sections };
    });
    setDragField(null);
  }, [dragField]);

  const updateText = useCallback((key: keyof PdfLayout['texts'], value: string | boolean) => {
    setLayout((prev) => ({ ...prev, texts: { ...prev.texts, [key]: value } }));
  }, []);

  const updateStyleNum = useCallback((key: keyof PdfLayout['style'], value: number) => {
    setLayout((prev) => ({ ...prev, style: { ...prev.style, [key]: value } }));
  }, []);

  const updateStyleColor = useCallback((key: keyof PdfLayout['style'], value: string) => {
    setLayout((prev) => ({ ...prev, style: { ...prev.style, [key]: value } }));
  }, []);

  // Generic style patch — used by the newer options (show logo, logo size,
  // document font) that are not number/color inputs.
  const updateStylePatch = useCallback((patch: Partial<PdfLayoutStyle>) => {
    setLayout((prev) => ({ ...prev, style: { ...prev.style, ...patch } }));
  }, []);

  const resetToDefaults = useCallback(async () => {
    if (!(await showConfirm(t('riderEditor.resetConfirm')))) return;
    setLayout(createDefaultPdfLayout());
    setSaveStatus('idle');
  }, []);

  // Apply a saved preset: replace the working layout (defaults merged so no
  // field is missing); the existing autosave persists it to the scope.
  const applyPreset = useCallback((data: any) => {
    setLayout(mergePdfLayouts(createDefaultPdfLayout(), data));
    setSaveStatus('idle');
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!canEditLayouts) return false;
    // Belt-and-braces: the GLOBAL template is admin-only and its tab is
    // hidden for anybody else — never write it from a stale scope value.
    if (scope === 'global' && !canEditGlobalTemplate) return false;
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const token = savedLastModifiedRef.current;
      let res: any;
      if (scope === 'global') {
        res = await apiSavePdfLayoutGlobal(layout, token);
      } else if (activeProfileId) {
        res = await apiSavePdfLayoutBand(activeProfileId, layout, token);
      } else {
        throw new Error(t('riderEditor.noBandSelected'));
      }
      // Baseline for the dirty test: exactly what we just persisted — plus the
      // NEW concurrency token returned by the server.
      savedSigRef.current = layoutSignature(layout);
      if (res?.lastModified != null) savedLastModifiedRef.current = res.lastModified;
      reportSaveOk('rider');
      setSaveStatus('saved');
      return true;
    } catch (err: any) {
      // CONFLICT (409): another tab/session wrote this layout after we loaded it.
      // Never overwrite the fresher version — reload it, re-baseline, and say so.
      if (isLayoutConflict(err)) {
        try {
          applyLoadedLayout(await fetchLayoutForScope());
        } catch {
          // Server unreachable: keep the working copy, the message still shows.
        }
        if (!isShowcaseMode()) setSaveError(t('riderEditor.conflictReloaded'));
        setSaveStatus('error');
        return false;
      }
      reportSaveError('rider', err);
      // Showcase (demo): never surface a save error — nothing is ever saved.
      if (!isShowcaseMode()) setSaveError(err?.message || t('riderEditor.saveFailed'));
      setSaveStatus('error');
      return false;
    }
  }, [canEditLayouts, scope, activeProfileId, layout, applyLoadedLayout, fetchLayoutForScope]);

  // ---- Autosave (debounced): every edit is persisted 800ms after it settles,
  // so switching band/scope/reloading never loses work. The manual Save button
  // remains for immediate persistence + explicit feedback.
  useEffect(() => {
    if (!loaded || !canEditLayouts) return;
    if (isShowcaseMode()) return; // demo account: read-only, never autosave
    // Never save a layout the user did not change (canonical signature).
    if (savedSigRef.current === null || savedSigRef.current === layoutSigRef.current) return;
    const timer = setTimeout(async () => {
      await save();
    }, 800);
    return () => clearTimeout(timer);
    // save is intentionally re-created on layout change (it captures `layout`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, loaded, canEditLayouts]);

  // ---- Live PDF preview (debounced WYSIWYG on the WORKING layout) ----
  const previewRiderDoc = useMemo(() => ({
    transport: (config?.rider?.transport as string) || '',
    activeMusicianIds: config?.rider?.activeMusicianIds || [],
    activeCrewIds: config?.rider?.activeCrewIds || [],
    requirements: null,
  }), [config]);

  useEffect(() => {
    if (!loaded || !canEditLayouts) return;
    let cancelled = false;
    const token = ++genTokenRef.current;
    const timer = setTimeout(async () => {
      try {
        setPdfError(null);
        // Backline images may be /static/uploads/... paths — pdfmake runs
        // server-side and cannot fetch them, so resolve to data URLs first
        // (same as RiderPage; without this the docDef 500s on /api/pdf).
        const [musiciansResolved, crewResolved] = await Promise.all([
          resolveBacklineImages(musicians),
          resolveBacklineImages(crew),
        ]);
        // LIGHT PLOTS (2026-11-04, retour utilisateur : « l'image du light plot
        // n'apparaît pas dans le preview du rider layout »). L'aperçu de l'ÉDITEUR
        // construisait son docDef sans `lightPlotImages` — 3ᵉ occurrence du même
        // défaut (deux constructions d'un même document finissent par diverger) :
        // la page Rider et la page show les résolvaient, l'éditeur non. Même
        // fabrique que RiderPage : union PARTAGÉE des 4 transports, PDF → PNG.
        const lightPlotImages = await resolveLightPlotsForPdf(
          activeProfileId,
          sharedLightPlots((config as any)?.lights),
        );
        const docDef = buildPdfDoc(
          previewBandName,
          previewRiderDoc as any,
          config as any,
          musiciansResolved as any,
          crewResolved as any,
          null,
          null,
          bandLogoUrl,
          dietById,
          undefined,
          teamManager,
          undefined,
          layout,
          null, // tourName : pas de show ici
          lightPlotImages,
        );
        if (cancelled || token !== genTokenRef.current) return;
        setPdfDoc(docDef);
      } catch (err: any) {
        if (cancelled || token !== genTokenRef.current) return;
        setPdfError(err?.message || t('riderEditor.previewFailed'));
        setPdfDoc(null);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [loaded, canEditLayouts, previewBandName, previewRiderDoc, config, musicians, crew, bandLogoUrl, teamManager, dietById, layout, activeProfileId]);

  if (!canEditLayouts) {
    return <LayoutAccessDenied titleKey="riderEditor.title" />;
  }

  return (
    <div
      className="page"
      // P3d QA marker: the canvas-editor chrome this lot harmonises.
      data-layout-editor="rider"
      // ---- « Entrée = valider maintenant » (2026-10-08) ----
      // Un SEUL écouteur sur le conteneur couvre TOUS les champs de l'éditeur (71
      // champs nombre + les textes) sans toucher aux ~70 sites d'édition. Avant, Entrée
      // ne faisait RIEN (mesuré : 0 requête, le focus restait dans le champ) et il
      // fallait sortir du champ pour que la saisie soit enregistrée. Entrée sort donc
      // du champ (déclenche son chemin de validation) ET enregistre tout de suite,
      // sans attendre les 800 ms de l'autosave.
      // Volontairement exclu : les zones multilignes (Entrée y fait un saut de ligne)
      // et les cases/radios.
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        const el = e.target as HTMLElement | null;
        if (!el || el.tagName !== 'INPUT') return;
        const type = el.getAttribute('type');
        if (type === 'checkbox' || type === 'radio') return;
        e.preventDefault();
        (el as HTMLInputElement).blur();
        void save();
      }}
      // ---- Borner À LA VALIDATION, jamais pendant la frappe (2026-10-08) ----
      // Avant, les champs bornés se recadraient en tapant : dans un `max=60`, taper
      // « 160 » ramenait à 60 → impossible de saisir la valeur voulue. Les handlers
      // `onChange` appliquent donc la valeur BRUTE, et la borne (`min`/`max` portés par
      // le champ lui-même) est posée ici, une seule fois, quand le champ est validé
      // (sortie du champ, ou Entrée via le handler ci-dessus qui déclenche ce blur).
      // On réinjecte la valeur corrigée par le setter natif pour que React la voie.
      onBlurCapture={(e) => {
        const el = e.target as HTMLInputElement | null;
        if (!el || el.tagName !== 'INPUT' || el.getAttribute('type') !== 'number') return;
        if (el.value === '' || Number.isNaN(Number(el.value))) return;
        const min = el.getAttribute('min');
        const max = el.getAttribute('max');
        const borned = Math.max(
          min === null ? -Infinity : Number(min),
          Math.min(max === null ? Infinity : Number(max), Number(el.value)),
        );
        if (borned === Number(el.value)) return;
        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (!setter) return;
        setter.call(el, String(borned));
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }}
    >
      <PageHeader
        title={withPageIcon('riderEditor.title', t('riderEditor.title'))}
        subtitle={t('riderEditor.subtitle')}
      />

      <div className="rider-editor-scope">
        <span className="nav-section-label">{t('riderEditor.scope')}</span>
        <div className="toggle-group">
          {canEditGlobalTemplate && (
            <button
              type="button"
              className={`btn ${scope === 'global' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setScope('global')}
            >
              {t('riderEditor.globalTemplate')}
            </button>
          )}
          <button
            type="button"
            className={`btn ${scope === 'band' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setScope('band')}
          >
            {t('riderEditor.thisBand')}
          </button>
        </div>
        {scope === 'band' && !activeProfileId && (
          <p className="empty-hint">{t('riderEditor.selectBandFirst')}</p>
        )}
      </div>

      <LayoutPresetsPanel
        kind="rider"
        scope={scope}
        bandId={activeProfileId}
        layout={layout}
        onApply={applyPreset}
      />

      <div className="rider-editor-grid">
        <div className="rider-editor-panel">
          {/* Running page header — like the footer but on top of every page.
              Kept at the TOP of the editor to mirror the PDF (first thing a
              reader sees). Disabled by default so the original look is unchanged. */}
          <div className="form-section">
            <h2>{t('riderEditor.style')}</h2>
            {/* Logo: show/hide + width (40-300pt, setlist pattern). When the
                logo is hidden the band name is printed instead. */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.showLogo')}</label>
                <label className="section-row-check">
                  <input
                    type="checkbox"
                    checked={layout.style.showLogo !== false}
                    onChange={(e) => updateStylePatch({ showLogo: e.target.checked })}
                  />
                  <span className="text-dim">{t('riderEditor.showLogoOn')}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.logoSize')}</label>
                <input
                  type="number"
                  min={40}
                  max={300}
                  className="form-input"
                  value={layout.style.logoWidth ?? 180}
                  onChange={(e) => updateStylePatch({ logoWidth: Number(e.target.value) || 180 })}
                  title={t('riderEditor.logoSizeHint')}
                />
              </div>
              {/* UPPERCASE data cells (2026-10-17, setlist pattern): global DEFAULT
                  for every rider table VALUE — each section can override it
                  (inherit/on/off) in its own style block; renders in the PDF
                  only, the stored data / i18n / web UI keep their case. */}
              <div className="form-group form-group--wide">
                <label className="form-field-label">{t('riderEditor.uppercase')}</label>
                <label className="section-row-check">
                  <input
                    type="checkbox"
                    checked={layout.style.uppercase === true}
                    onChange={(e) => updateStylePatch({ uppercase: e.target.checked })}
                  />
                  <span className="text-dim">{t('riderEditor.uppercaseOn')}</span>
                </label>
              </div>
              {/* VENUE HIGHLIGHT (2026-10-18, user): subtle fill on every row
                  provided by the venue, across Backline / Mic Stands / Wireless
                  Transmitters. Global style only; off by default. */}
              <div className="form-group form-group--wide">
                <label className="form-field-label">{t('riderEditor.venueHighlight')}</label>
                <label className="section-row-check">
                  <input
                    type="checkbox"
                    checked={layout.style.venueHighlight === true}
                    onChange={(e) => updateStylePatch({ venueHighlight: e.target.checked })}
                  />
                  <span className="text-dim">{t('riderEditor.venueHighlightOn')}</span>
                </label>
                <input
                  type="color"
                  className="form-input"
                  style={{ padding: 0, height: 30 }}
                  value={layout.style.venueHighlightColor || '#FFF9C4'}
                  onChange={(e) => updateStylePatch({ venueHighlightColor: e.target.value })}
                  disabled={layout.style.venueHighlight !== true}
                  title={t('riderEditor.venueHighlightColor')}
                />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.font')}</label>
                <select
                  className="form-input"
                  value={layout.style.fontFamily ?? 'Roboto'}
                  onChange={(e) => updateStylePatch({ fontFamily: e.target.value as RiderFontFamily })}
                >
                  {RIDER_FONT_FAMILIES.map((fam) => (
                    <option key={fam} value={fam}>{fam}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.headingColor')}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.headingColor} onChange={(e) => updateStyleColor('headingColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.headingSize')}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.headingFontSize} onChange={(e) => updateStyleNum('headingFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.ruleColor')}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.ruleColor} onChange={(e) => updateStyleColor('ruleColor', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.titleSize')}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.titleFontSize} onChange={(e) => updateStyleNum('titleFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.subtitleSize')}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.subtitleFontSize} onChange={(e) => updateStyleNum('subtitleFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.bodySize')}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.bodyFontSize} onChange={(e) => updateStyleNum('bodyFontSize', Number(e.target.value))} />
              </div>
            </div>
            {/* Text weights (2026-09-15) — bold for the title, the subtitles
                (tour name + version line) and the section headings. All three
                default to ON, which keeps the historical rendering; the version
                template line was italic-only and is now bold+italic. */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.headingBold')}</label>
                <label className="section-row-check">
                  <input
                    type="checkbox"
                    checked={layout.style.headingBold !== false}
                    onChange={(e) => updateStylePatch({ headingBold: e.target.checked })}
                  />
                  <span className="text-dim">{t('riderEditor.boldOn')}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.titleBold')}</label>
                <label className="section-row-check">
                  <input
                    type="checkbox"
                    checked={layout.style.titleBold !== false}
                    onChange={(e) => updateStylePatch({ titleBold: e.target.checked })}
                  />
                  <span className="text-dim">{t('riderEditor.boldOn')}</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.subtitleBold')}</label>
                <label className="section-row-check">
                  <input
                    type="checkbox"
                    checked={layout.style.subtitleBold !== false}
                    onChange={(e) => updateStylePatch({ subtitleBold: e.target.checked })}
                  />
                  <span className="text-dim">{t('riderEditor.boldOn')}</span>
                </label>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.textColor')}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.textColor} onChange={(e) => updateStyleColor('textColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.footerSize')}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.footerFontSize} onChange={(e) => updateStyleNum('footerFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t('riderEditor.footerColor')}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.footerColor} onChange={(e) => updateStyleColor('footerColor', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>{t('riderEditor.headerEveryPage')}</h2>
            <label className="section-row-check" style={{ marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!layout.texts.headerEnabled} onChange={() => updateText('headerEnabled', !layout.texts.headerEnabled)} />
              <span>{t('riderEditor.enableRunningHeader')}</span>
            </label>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('riderEditor.headerLeft')}</label>
                <input className="form-input" value={layout.texts.headerLeftTemplate} onChange={(e) => updateText('headerLeftTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('riderEditor.headerCenter')}</label>
                <input className="form-input" value={layout.texts.headerCenterTemplate} onChange={(e) => updateText('headerCenterTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('riderEditor.headerRight')}</label>
                <input className="form-input" value={layout.texts.headerRightTemplate} onChange={(e) => updateText('headerRightTemplate', e.target.value)} />
              </div>
            </div>
            <p className="text-dim" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
              {t('layoutEditor.placeholdersLabel')} {'{bandName}'} {'{versionDetail}'} {'{versionLabel}'} {'{showName}'} {'{showDate}'} {'{page}'} {'{pages}'}
            </p>
          </div>

          <div className="form-section">
            <h2>{t('riderEditor.sections')}</h2>
            <p style={{ color: 'var(--text-dim)', margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
              {t('riderEditor.sectionsHint')}
            </p>
            {layout.sections.map((sec, i) => {
              const textFields = SECTION_TEXT_FIELDS[sec.id] || [];
              // Input List / Output List / Rack Usage are PURE tables: their `fields`
              // list is the table's columns (show/hide/reorder), not free-form
              // fields — so the "Add space" / "Add text" buttons don't apply
              // (they could never render inside a table) and the list is
              // labelled "Columns". `rack-usage` was added 2026-09-16: the
              // intro-spacer bug audit showed its Add-space button was a silent
              // no-op (the builder emits a table, not layout fields).
              // Backline (v0.641) is ALSO a COLUMN section (same show/hide/
              // reorder UX, list labelled "Columns") but it KEEPS the Add
              // buttons: the table is emitted at the position of its first
              // enabled column, so a spacer / free text above or below the
              // table renders fine.
              const hasColumns = sec.id === 'input-list' || sec.id === 'output-list' || sec.id === 'rack-usage' || sec.id === 'backline' || sec.id === 'mic-stands';
              const showAddButtons = sec.id !== 'input-list' && sec.id !== 'output-list' && sec.id !== 'rack-usage';
              // Per-section style override: which keys are set (absent = inherit
              // the global style) and whether the inline block is expanded.
              const ov = (sec.styleOverride || {}) as Partial<PdfLayoutStyle>;
              const overrideCount = Object.keys(ov).length;
              const overrideOpen = !!openStyleOverrides[sec.id];
              return (
                <div key={sec.id} className="section-card" data-section-id={sec.id}>
                  <div className="section-row">
                    <div className="section-row-reorder">
                      <button type="button" className="btn btn-small" onClick={() => moveSection(i, -1)} disabled={i === 0} title={t('riderEditor.moveUp')}>↑</button>
                      <button type="button" className="btn btn-small" onClick={() => moveSection(i, 1)} disabled={i === layout.sections.length - 1} title={t('riderEditor.moveDown')}>↓</button>
                    </div>
                    <label className="section-row-check">
                      <input type="checkbox" checked={sec.enabled} onChange={() => toggleEnabled(i)} />
                      <span className="text-dim" title={sec.id}>{DEFAULT_TITLES[sec.id] || sec.id}</span>
                    </label>
                    <input
                      className="form-input section-row-title"
                      value={sec.title ?? ''}
                      placeholder={DEFAULT_TITLES[sec.id] || t('riderEditor.noHeading')}
                      onChange={(e) => setTitle(i, e.target.value)}
                    />
                    <label className="section-row-check" title={t('riderEditor.startNewPage')}>
                      <input type="checkbox" checked={!!sec.pageBreakBefore} onChange={() => togglePageBreak(i)} />
                      <span className="text-dim">{t('riderEditor.newPage')}</span>
                    </label>
                    {/* Section style (override) toggle — opens an inline block
                        under the row with this section's local style fields. */}
                    <button
                      type="button"
                      className={`btn btn-small section-style-toggle ${overrideCount > 0 ? 'section-style-toggle-on' : ''}`}
                      onClick={() => toggleSectionOverride(sec.id)}
                      title={t('riderEditor.styleOverrideTitle')}
                      aria-expanded={overrideOpen}
                    >
                      Aa{overrideCount > 0 ? ` ${overrideCount}` : ''}
                    </button>
                  </div>
                  {overrideOpen && (
                    <div className="section-style-override">
                      <div className="section-style-override-head">
                        <span className="section-style-override-title">{t('riderEditor.styleOverride')}</span>
                        <span className="text-dim">{t('riderEditor.styleOverrideHint')}</span>
                        <button
                          type="button"
                          className="btn btn-small"
                          onClick={() => resetSectionStyleOverride(i)}
                          disabled={overrideCount === 0}
                        >
                          {t('riderEditor.styleOverrideReset')}
                        </button>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-field-label">{t('riderEditor.font')}</label>
                          <select
                            className="form-input"
                            value={ov.fontFamily ?? ''}
                            onChange={(e) => updateSectionStyleOverride(i, 'fontFamily', e.target.value || undefined)}
                          >
                            <option value="">{t('riderEditor.styleInherit')}</option>
                            {RIDER_FONT_FAMILIES.map((fam) => (
                              <option key={fam} value={fam}>{fam}</option>
                            ))}
                          </select>
                        </div>
                        {SECTION_STYLE_NUM_FIELDS.map(({ key, labelKey }) => (
                          <div className="form-group" key={key}>
                            <label className="form-field-label">{t(`riderEditor.${labelKey}`)}</label>
                            <div className="section-style-override-input">
                              <input
                                type="number"
                                step="0.5"
                                className="form-input"
                                value={ov[key] ?? ''}
                                placeholder={String(layout.style[key] ?? '')}
                                onChange={(e) => updateSectionStyleOverride(i, key, e.target.value === '' ? undefined : Number(e.target.value))}
                              />
                              <button
                                type="button"
                                className="btn btn-small"
                                title={t('riderEditor.styleOverrideClear')}
                                disabled={ov[key] === undefined}
                                onClick={() => updateSectionStyleOverride(i, key, undefined)}
                              >
                                ↺
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* UPPERCASE per section (2026-10-17): inherit/on/off — the
                          section override wins over the global Style default. */}
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-field-label">{t('riderEditor.uppercase')}</label>
                          <div className="section-style-override-input">
                            <select
                              className="form-input"
                              value={ov.uppercase === undefined ? '' : (ov.uppercase ? 'on' : 'off')}
                              onChange={(e) => updateSectionStyleOverride(i, 'uppercase', e.target.value === '' ? undefined : e.target.value === 'on')}
                            >
                              <option value="">{t('riderEditor.styleInherit')}</option>
                              <option value="on">{t('riderEditor.styleOn')}</option>
                              <option value="off">{t('riderEditor.styleOff')}</option>
                            </select>
                            <button
                              type="button"
                              className="btn btn-small"
                              title={t('riderEditor.styleOverrideClear')}
                              disabled={ov.uppercase === undefined}
                              onClick={() => updateSectionStyleOverride(i, 'uppercase', undefined)}
                            >
                              ↺
                            </button>
                          </div>
                          <span className="text-dim" style={{ fontSize: 11 }}>{t('riderEditor.uppercaseOn')}</span>
                        </div>
                      </div>
                      {/* SHOW EMPTY ROWS (2026-10-18, user): OUTPUT list only — the
                          Input list keeps its historical holes rule. */}
                      {(sec.id === 'output-list') && (
                        <div className="form-group">
                          <label className="form-field-label">{t('riderEditor.showEmptyRows')}</label>
                          <div className="section-style-override-input">
                            <select
                              className="form-input"
                              value={ov.showEmptyRows === undefined ? '' : (ov.showEmptyRows ? 'on' : 'off')}
                              onChange={(e) => updateSectionStyleOverride(i, 'showEmptyRows', e.target.value === '' ? undefined : e.target.value === 'on')}
                            >
                              <option value="">{t('riderEditor.styleInherit')}</option>
                              <option value="on">{t('riderEditor.styleOn')}</option>
                              <option value="off">{t('riderEditor.styleOff')}</option>
                            </select>
                            <button
                              type="button"
                              className="btn btn-small"
                              title={t('riderEditor.styleOverrideClear')}
                              disabled={ov.showEmptyRows === undefined}
                              onClick={() => updateSectionStyleOverride(i, 'showEmptyRows', undefined)}
                            >
                              ↺
                            </button>
                          </div>
                          <span className="text-dim" style={{ fontSize: 11 }}>{t('riderEditor.showEmptyRowsOn')}</span>
                        </div>
                      )}
                      <div className="form-row">
                        {SECTION_STYLE_COLOR_FIELDS.map(({ key, labelKey }) => (
                          <div className="form-group" key={key}>
                            <label className="form-field-label">{t(`riderEditor.${labelKey}`)}</label>
                            <div className="section-style-override-input">
                              <input
                                type="color"
                                className="form-input"
                                style={{ padding: 0, height: 38 }}
                                value={ov[key] ?? (layout.style[key] as string)}
                                onChange={(e) => updateSectionStyleOverride(i, key, e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-small"
                                title={t('riderEditor.styleOverrideClear')}
                                disabled={ov[key] === undefined}
                                onClick={() => updateSectionStyleOverride(i, key, undefined)}
                              >
                                ↺
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="section-card-body">
                    {sec.enabled && (
                      <ul className="section-detail-list">
                        {(SECTION_DETAILS[sec.id] || []).map((d) => (
                          <li key={d}>{d}</li>
                        ))}
                      </ul>
                    )}
                    {(!hasColumns || (Array.isArray(sec.fields) && sec.fields.length > 0)) && (
                      <div className="field-list">
                        <div className="field-list-head">
                          <span>{hasColumns ? t('riderEditor.columns') : t('riderEditor.fields')}</span>
                          <span className="text-dim">{hasColumns ? t('riderEditor.columnsHint') : t('riderEditor.fieldsHint')}</span>
                        </div>
                        {showAddButtons && (
                        <div className="field-list-add">
                          <button type="button" className="btn btn-small field-spacer-add" onClick={() => addSpacer(i)} title={t('riderEditor.addSpaceTitle')}>
                            {t('riderEditor.addSpace')}
                          </button>
                          <button type="button" className="btn btn-small field-text-add" onClick={() => addText(i)} title={t('riderEditor.addTextTitle')}>
                            {t('riderEditor.addText')}
                          </button>
                        </div>
                        )}
                        {showAddButtons && (!sec.fields || sec.fields.length === 0) && (
                          <p className="text-dim field-list-empty" style={{ margin: '0.25rem 0 0', fontSize: '0.85rem' }}>
                            {t('riderEditor.noFields')}
                          </p>
                        )}
                        {(sec.fields || []).map((f, fi) => {
                          const hasContent = fieldHasContent(f.id);
                          const isEmpty = !isSpacer(f.id) && !isTextField(f.id) && !hasContent;
                          // Canonical label resolution (v0.651): a KNOWN field
                          // id displays the canonical label from
                          // DEFAULT_SECTION_FIELDS — NOT the stored `label`,
                          // which may be a stale value from an old layout (the
                          // KADEBOSTANY layout stored `backline.item` as
                          // 'Item'). Unknown ids (user spacer / free text)
                          // fall back to their stored label.
                          // Libellé : canonique pour un id CONNU (auto-réparation des
                          // layouts anciens) puis TRADUIT via `riderLayoutFields.<id>`
                          // (§ Lot 3, 2026-11-04). Un id inconnu garde son libellé saisi.
                          const displayLabel = fieldLabel(f.id, f.label || f.id, t);
                          // Per-field style override (v0.558): the stored keys,
                          // the EFFECTIVE count (legacy flat keys of a free-text
                          // field included) and whether the block is expanded.
                          const fov = (f.styleOverride || {}) as Partial<PdfLayoutStyle>;
                          const fovCount = Object.keys(fieldStyleOverride(f) || {}).length;
                          const fovOpen = !!openFieldStyles[`${sec.id}:${fi}`];
                          return (
                          <Fragment key={f.id + (f.label || '') + fi}>
                          <div
                            className={`field-row ${isSpacer(f.id) ? 'field-row-spacer' : ''} ${isTextField(f.id) ? 'field-row-text' : ''} ${isEmpty ? 'field-row-empty' : ''} ${dragField?.fieldIndex === fi && dragField.sectionIndex === i ? 'field-row-dragging' : ''}`}
                            data-field-id={f.id}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); moveFieldTo(i, fi); }}
                            onDragEnd={() => setDragField(null)}
                          >
                            <div className="section-row-reorder">
                              <span
                                className="field-drag-handle"
                                draggable
                                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragField({ sectionIndex: i, fieldIndex: fi }); }}
                                title={t('riderEditor.dragToReorder')}
                              >⋮⋮</span>
                              <button type="button" className="btn btn-small" onClick={() => moveField(i, fi, -1)} disabled={fi === 0} title={t('riderEditor.moveUp')}>↑</button>
                              <button type="button" className="btn btn-small" onClick={() => moveField(i, fi, 1)} disabled={fi === (sec.fields || []).length - 1} title={t('riderEditor.moveDown')}>↓</button>
                            </div>
                            <label className="section-row-check">
                              <input type="checkbox" checked={f.enabled !== false} onChange={() => toggleField(i, fi)} />
                            </label>
                            <span className="field-label">{displayLabel}</span>
                            {isEmpty && (
                              <span className="field-empty-hint" title={t('riderEditor.emptyHint')}>{t('riderEditor.empty')}</span>
                            )}
                            {isSpacer(f.id) ? (
                              <>
                                <input
                                  type="number"
                                  min={0}
                                  max={60}
                                  className="form-input field-spacer-size"
                                  value={Number(f.size) || 8}
                                  onChange={(e) => updateSpacerSize(i, fi, Number(e.target.value) || 0)}
                                  title={t('riderEditor.spaceHeight')}
                                />
                                <button type="button" className="btn btn-small" onClick={() => duplicateField(i, fi)} title={t('riderEditor.duplicateSpace')}>⧉</button>
                                <button type="button" className="btn btn-small btn-danger" onClick={() => removeSpacer(i, fi)} title={t('riderEditor.removeSpace')}>✕</button>
                              </>
                            ) : isTextField(f.id) ? (
                              <>
                                <div className="field-text-editor">
                                  <input
                                    className="form-input"
                                    value={f.textContent ?? ''}
                                    onChange={(e) => updateTextField(i, fi, { textContent: e.target.value })}
                                    placeholder={t('riderEditor.textPlaceholder')}
                                  />
                                  {/* Formatting lives in the "Aa" style override block
                                      of the row (bold / italic / size / colour /
                                      alignment) — no duplicated toolbar here. */}
                                </div>
                                <button type="button" className="btn btn-small" onClick={() => duplicateField(i, fi)} title={t('riderEditor.duplicateText')}>⧉</button>
                                <button type="button" className="btn btn-small btn-danger" onClick={() => removeSpacer(i, fi)} title={t('riderEditor.removeText')}>✕</button>
                              </>
                            ) : f.link && f.id !== 'general-info.travel-party' ? (
                              <a
                                className="field-link"
                                href={`${bandBase}/${f.link}?focus=${encodeURIComponent(f.id)}`}
                                onClick={(e) => { e.preventDefault(); navigate(`${bandBase}/${f.link}?focus=${encodeURIComponent(f.id)}`); }}
                                title={t('riderEditor.editIn', { label: displayLabel, link: f.link })}
                              >
                                {t('riderEditor.edit')}
                              </a>
                            ) : null}
                            {f.id === 'general-info.travel-party' && (
                              <span className="field-link text-dim" title={t('riderEditor.computedAtGenTitle')}>
                                {t('riderEditor.computedAtGen')}
                              </span>
                            )}
                            {/* Column WIDTH (v0.644) — TABLE-COLUMN sections
                                (input-list / output-list / rack-usage /
                                backline / mic-stands; their `fields` list
                                doubles as the table's columns) PLUS the 4
                                Lights FIXTURE columns (v0.648) — a table that
                                lives INSIDE the Lights section, so it is NOT a
                                `hasColumns` section (its content lines keep
                                their normal field row, no width input).
                                Placeholder = the column's DEFAULT width
                                ('auto' / a number / '*'); a typed number
                                persists as field.width; ↺ clears it back to
                                the default. Persisted by the autosave. */}
                            {(hasColumns || LIGHT_FIXTURE_COLUMN_IDS.includes(f.id)) && !isSpacer(f.id) && !isTextField(f.id) && (
                              <div className="field-width-input">
                                <input
                                  type="number"
                                  min={10}
                                  className="form-input field-width-num"
                                  value={typeof f.width === 'number' ? f.width : ''}
                                  placeholder={String(DEFAULT_COLUMN_WIDTHS[f.id] ?? 'auto')}
                                  title={t('riderEditor.columnWidthTitle')}
                                  aria-label={t('riderEditor.columnWidth')}
                                  onChange={(e) => updateFieldWidth(i, fi, e.target.value === '' ? undefined : Number(e.target.value))}
                                />
                                <button
                                  type="button"
                                  className="btn btn-small"
                                  title={t('riderEditor.columnWidthReset')}
                                  disabled={typeof f.width !== 'number'}
                                  onClick={() => updateFieldWidth(i, fi, undefined)}
                                >↺</button>
                              </div>
                            )}
                            {/* Per-FIELD style (v0.558) — same "Aa" affordance as
                                the section rows: opens an inline block under
                                THIS field. Hidden for spacers (no text to
                                style). */}
                            {!isSpacer(f.id) && (
                              <button
                                type="button"
                                className={`btn btn-small field-style-toggle ${fovCount > 0 ? 'section-style-toggle-on' : ''}`}
                                onClick={() => toggleFieldStyle(i, fi)}
                                title={t('riderEditor.fieldStyleTitle')}
                                aria-expanded={fovOpen}
                              >
                                Aa{fovCount > 0 ? ` ${fovCount}` : ''}
                              </button>
                            )}
                          </div>
                          {fovOpen && !isSpacer(f.id) && (
                            <div className="section-style-override field-style-override">
                              <div className="section-style-override-head">
                                <span className="section-style-override-title">{t('riderEditor.fieldStyle')}</span>
                                <span className="text-dim">{t('riderEditor.fieldStyleHint')}</span>
                                <button
                                  type="button"
                                  className="btn btn-small"
                                  onClick={() => resetFieldStyleOverride(i, fi)}
                                  disabled={fovCount === 0}
                                >
                                  {t('riderEditor.fieldStyleReset')}
                                </button>
                              </div>
                              <div className="form-row">
                                {FIELD_STYLE_FIELDS.slice(0, 3).map(({ key, labelKey, kind }) => (
                                  <div className="form-group" key={key}>
                                    <label className="form-field-label">{t(`riderEditor.${labelKey}`)}</label>
                                    <div className="section-style-override-input">
                                      {fieldStyleControl(i, fi, key, kind, fov)}
                                      <button
                                        type="button"
                                        className="btn btn-small"
                                        title={t('riderEditor.styleOverrideClear')}
                                        disabled={fov[key] === undefined}
                                        onClick={() => updateFieldStyleOverride(i, fi, key, undefined)}
                                      >
                                        ↺
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="form-row">
                                {FIELD_STYLE_FIELDS.slice(3).map(({ key, labelKey, kind }) => (
                                  <div className="form-group" key={key}>
                                    <label className="form-field-label">{t(`riderEditor.${labelKey}`)}</label>
                                    <div className="section-style-override-input">
                                      {fieldStyleControl(i, fi, key, kind, fov)}
                                      <button
                                        type="button"
                                        className="btn btn-small"
                                        title={t('riderEditor.styleOverrideClear')}
                                        disabled={fov[key] === undefined}
                                        onClick={() => updateFieldStyleOverride(i, fi, key, undefined)}
                                      >
                                        ↺
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          </Fragment>
                          );
                        })}
                      </div>
                    )}
                    {sec.id === 'title' && (
                      <div className="form-group section-text-inline">
                        <label className="section-row-check" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={layout.texts.showTourName !== false}
                            onChange={(e) => updateText('showTourName', e.target.checked)}
                          />
                          <span>{t('riderEditor.showTourName')}</span>
                        </label>
                      </div>
                    )}
                    {/* PATCH-MARKS LEGEND (2026-10-23, user v2): ONE text line
                        PER colour used on a marked track of the band — swatch +
                        editable text. The colours come from the tracks (no
                        free-typing of hex). Saved in
                        texts.inputListPatchLegends[colour]. */}
                    {sec.id === 'input-list' && (
                      <div className="form-group section-text-inline" data-patch-legends>
                        <label className="form-field-label">{t('riderEditor.patchLegendsTitle')}</label>
                        {(() => {
                          const legends = (layout.texts as any).inputListPatchLegends || {};
                          const usedColours: string[] = [];
                          const scan = (list: any[]) => {
                            for (const p of list || []) {
                              for (const inst of p.instruments || []) for (const tr of inst.tracks || []) {
                                const c = typeof tr.patchMark === 'string' ? tr.patchMark.toLowerCase() : '';
                                if (c && !usedColours.includes(c)) usedColours.push(c);
                              }
                              // Monitor-RETURN marks too (v0.1.63 fix, user): a colour
                              // used ONLY on an IEM/wedge/sub return must get its
                              // legend line in the layout editor as well.
                              for (const k of ['iemPatchMark', 'wedgePatchMark', 'subPatchMark']) {
                                const c = typeof p[k] === 'string' ? p[k].toLowerCase() : '';
                                if (c && !usedColours.includes(c)) usedColours.push(c);
                              }
                            }
                          };
                          scan(musicians as any[]);
                          scan(crew as any[]);
                          const extra = Object.keys(legends).filter((c) => !usedColours.includes(c.toLowerCase()));
                          const rows = [...usedColours, ...extra];
                          if (rows.length === 0) {
                            return <p style={{ color: 'var(--text-dim)', margin: '4px 0' }}>{t('riderEditor.patchLegendsNone')}</p>;
                          }
                          return rows.map((c) => (
                            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <span style={{ width: 18, height: 18, borderRadius: 4, background: c, border: '1px solid var(--border)', flexShrink: 0 }} />
                              <input
                                className="form-input"
                                value={legends[c] ?? legends[c.toLowerCase()] ?? ''}
                                placeholder={t('riderEditor.patchLegendPh')}
                                onChange={(e) => {
                                  const next = { ...(layout.texts as any).inputListPatchLegends, [c]: e.target.value };
                                  updateText('inputListPatchLegends' as never, next);
                                }}
                                data-patch-legend-input={c}
                                style={{ flex: 1 }}
                              />
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                    {/* OUTPUT-LIST legend (user v3): a colour can mean something
                        DIFFERENT on the outputs than on the inputs — its own
                        editable text per colour, saved in
                        texts.outputListPatchLegends[colour]. Colours come from
                        the monitor-return marks (iem/wedge/sub) + output tracks. */}
                    {sec.id === 'output-list' && (
                      <div className="form-group section-text-inline" data-patch-legends-output>
                        <label className="form-field-label">{t('riderEditor.patchLegendsTitle')}</label>
                        {(() => {
                          const legends = (layout.texts as any).outputListPatchLegends || {};
                          const usedColours: string[] = [];
                          const scan = (list: any[]) => {
                            for (const p of list || []) {
                              for (const k of ['iemPatchMark', 'wedgePatchMark', 'subPatchMark']) {
                                const c = typeof p[k] === 'string' ? p[k].toLowerCase() : '';
                                if (c && !usedColours.includes(c)) usedColours.push(c);
                              }
                              for (const inst of p.instruments || []) for (const tr of inst.tracks || []) {
                                if ((tr as any).direction !== 'output') continue;
                                const c = typeof tr.patchMark === 'string' ? tr.patchMark.toLowerCase() : '';
                                if (c && !usedColours.includes(c)) usedColours.push(c);
                              }
                            }
                          };
                          scan(musicians as any[]);
                          scan(crew as any[]);
                          const extra = Object.keys(legends).filter((c) => !usedColours.includes(c.toLowerCase()));
                          const rows = [...usedColours, ...extra];
                          if (rows.length === 0) {
                            return <p style={{ color: 'var(--text-dim)', margin: '4px 0' }}>{t('riderEditor.patchLegendsNone')}</p>;
                          }
                          return rows.map((c) => (
                            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <span style={{ width: 18, height: 18, borderRadius: 4, background: c, border: '1px solid var(--border)', flexShrink: 0 }} />
                              <input
                                className="form-input"
                                value={legends[c] ?? legends[c.toLowerCase()] ?? ''}
                                placeholder={t('riderEditor.patchLegendPh')}
                                onChange={(e) => {
                                  const next = { ...(layout.texts as any).outputListPatchLegends, [c]: e.target.value };
                                  updateText('outputListPatchLegends' as never, next);
                                }}
                                data-patch-legend-output-input={c}
                                style={{ flex: 1 }}
                              />
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                    {textFields.map((tk) => (
                      <div key={tk} className="form-group section-text-inline">
                        <label className="form-field-label">{t((TEXT_FIELD_LABELS as Record<string, string>)[tk])}</label>
                        {tk === 'introText' ? (
                          <AutoTextarea className="form-input" rows={3} value={layout.texts[tk]} onChange={(e) => updateText(tk, e.target.value)} />
                        ) : (
                          <input className="form-input" value={String(layout.texts[tk])} onChange={(e) => updateText(tk, e.target.value)} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer renders on every page of the rider — kept at the end, in
              document order, right after the last section. */}
          <div className="form-section">
            <h2>{t('riderEditor.footerEveryPage')}</h2>
            <label className="section-row-check" style={{ marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={layout.texts.footerEnabled !== false} onChange={() => updateText('footerEnabled', layout.texts.footerEnabled === false)} />
              <span>{t('riderEditor.enableRunningFooter')}</span>
            </label>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('riderEditor.footerLeft')}</label>
                <input className="form-input" value={layout.texts.footerLeftTemplate} onChange={(e) => updateText('footerLeftTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('riderEditor.footerCenter')}</label>
                <input className="form-input" value={layout.texts.footerCenterTemplate} onChange={(e) => updateText('footerCenterTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('riderEditor.footerRight')}</label>
                <input className="form-input" value={layout.texts.footerRightTemplate} onChange={(e) => updateText('footerRightTemplate', e.target.value)} />
              </div>
            </div>
            <p className="text-dim" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
              {t('layoutEditor.placeholdersLabel')} {'{bandName}'} {'{versionDetail}'} {'{versionLabel}'} {'{showName}'} {'{showDate}'} {'{page}'} {'{pages}'}
            </p>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={resetToDefaults}>{t('riderEditor.resetToDefaults')}</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saveStatus === 'saving'}>
              {saveStatus === 'saving' ? t('riderEditor.saving') : t('riderEditor.save')}
            </button>
            {saveStatus === 'saved' && <span className="save-status-ok">{t('riderEditor.saved')}</span>}
            {saveStatus === 'error' && saveError && <span className="save-status-err">{t('riderEditor.error', { msg: saveError })}</span>}
          </div>
        </div>

        <div className="rider-editor-preview">
          <h2>
            {t('riderEditor.pdfPreview')}
            <span className="preview-scope-badge">
              {scope === 'band' ? (bandName || t('riderEditor.thisBand')) : t('riderEditor.globalTemplate')}
            </span>
          </h2>
          {pdfError ? (
            <div className="rider-preview-placeholder rider-preview-error">{t('riderEditor.previewError', { msg: pdfError })}</div>
          ) : pdfDoc ? (
            <PdfPreview docDef={pdfDoc} />
          ) : (
            <div className="rider-preview-placeholder">
              {scope === 'band' && !activeProfileId ? t('riderEditor.selectBandToPreview') : t('riderEditor.generatingPreview')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
